import { debounce } from 'lodash';
import mezr from 'mezr';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Subtract } from 'utility-types';
export type AppScroller = {
    registerPart: (container: HTMLElement, url: string, title?: string) => void;
    unRegisterPart: (url: string) => void;
    setContainer: (container: React.RefObject<HTMLDivElement>) => void;
    goTo: (sectionId: string) => void;
    Top: () => void;
    currentUrl?: string;
    parts: part[];
};

const initState: AppScroller = {
    registerPart: 0 as never,
    unRegisterPart: 0 as never,
    setContainer: 0 as never,
    goTo: 0 as never,
    Top: 0 as never,
    parts: []

}

const AppScrollerReactContext = React.createContext<AppScroller>(initState);

export type AppScrollerProdiverProps =
    {
        refresh?: number
    };
type Props = RouteComponentProps<any> & AppScrollerProdiverProps;

type part = {
    url: string,
    title?: string,
    elt: HTMLElement,
    top: number,
    bottom: number,
    height: number,
    stickies: Array<{
        elt: HTMLElement;
        endTop: number;
        startTop: number;
    }>,
    sections: Array<{
        id: string;
        elt: HTMLElement;
        top: number,
        bottom: number,
    }>
};
type State = AppScroller & {
    container?: React.RefObject<HTMLDivElement>;
};

class Container extends React.Component<InjectedAppScrollerProps> {
    private container = React.createRef<HTMLDivElement>();
    componentDidMount() {
        const { appScrollerCtx: { setContainer } } = this.props;
        setContainer(this.container);
    }
    render() {
        const { children } = this.props;
        return (
            <div
                style={{
                    pointerEvents: 'initial',
                    position: 'relative',
                    height: '100%',
                }}
                ref={this.container}
            >
                {children}
            </div>
        );
    }
}

// tslint:disable-next-line: max-classes-per-file
class AppScrollerProvider extends React.Component<Props, State> {

    private y = 0;

    private h = 0;
    // private max = 0;

    // private h = 0;

    // private ratio = 0;
    private afId = 0;
    private instanceCurrentURL: string;

    private checkCurrent = debounce(() => {
        const { state: { parts } } = this;
        const isValid = (p: { top: number; bottom: number; }) => p && p.top < (this.y + this.h) && p.bottom > this.y;
        // find current = 
        const currentPart = [...parts].find((p, i, arr) => isValid(p) && !isValid(arr[i + 1]));

        const currentSection = currentPart?.sections.find((s, i, arr) => isValid(s) && !isValid(arr[i + 1]));
        // find section 
        const url = currentPart && (currentPart.url + (currentSection ? ('/' + currentSection.id) : '')) || '';

        if (this.instanceCurrentURL !== url) {
            this.instanceCurrentURL = url;
            this._setCurrentUrl(url);
        }
    }, 100);

    constructor(props: Props) {
        super(props);
        this._onscroll = this._onscroll.bind(this);
        this._refreshPositions = this._refreshPositions.bind(this);
        this._didSetContainer = this._didSetContainer.bind(this);
        this._onAnimationFrame = this._onAnimationFrame.bind(this);
        this._refresh = this._refresh.bind(this);
        this.state = {
            registerPart: this._registerPart.bind(this),
            unRegisterPart: this._unRegisterPart.bind(this),
            setContainer: this._setContainer.bind(this),
            goTo: this._gotToSection.bind(this),
            Top: this._Top.bind(this),
            parts: []
        };
    }
    componentDidUpdate(oldProps: Props) {
        if (oldProps.refresh !== this.props.refresh) {
            this._refresh();
        }
        const { location: { pathname, state }, history: { action } } = this.props;
        if (oldProps.location.pathname !== pathname && (state !== "scroll" || action !== 'PUSH')) {
            window.scrollTo(window.scrollX, window.scrollY + 1);
            setTimeout(() => this._GoToPart(pathname), 50);
        }
    }

    componentDidMount() {
        document.addEventListener("scroll", this._onscroll);
        window.addEventListener("resize", this._refresh);
        this._refreshHeights();
        this._refreshPositions();
    }

    componentWillUnmount() {
        document.removeEventListener("scroll", this._onscroll);
        window.removeEventListener("resize", this._refresh);
    }

    public render() {
        const { children } = this.props;
        return (
            <AppScrollerReactContext.Provider value={this.state}>
                {children}
            </AppScrollerReactContext.Provider>
        );
    }
    private _setContainer(container: React.RefObject<HTMLDivElement>) {
        this.setState(
            () => ({ container }),
            this._didSetContainer
        );
    }

    private _GoToPart(url: string, behavior?: ScrollBehavior) {
        const { parts, currentUrl } = this.state;
        const newCurrent = parts.find(p => url.indexOf(p.url) === 0);
        if (newCurrent && url !== currentUrl) {
            this._gotToSection(url, behavior);
        }
    }
    private _didSetContainer() {
        this._refreshHeights();
        this._refreshPositions();
        setTimeout(() => {
            this._refreshHeights();
            this._refreshPositions();
            this._GoToPart(this.props.location.pathname, 'auto');
        }, 100);
        // goto url
    }

    private _Top() {
        return this._gotToSection();
    }
    private _getUrlTop(url: string) {
        const { parts } = this.state;
        const currentPart = parts.find(p => url.indexOf(p.url) === 0);
        if (!currentPart) { return; }

        const sectionId = url.split('/').pop();
        const { top, sections } = currentPart;

        const section = sectionId && sections.find(s => s.id === sectionId);
        return Math.max((section && section.top && (section.top - 40) || (top + 1)), 0);
    }

    private _gotToSection(url?: string, behavior?: ScrollBehavior) {
        window.scrollTo({
            top: url && this._getUrlTop(url) || 0,
            behavior: behavior || 'smooth'
        });
    }

    private _onscroll() {
        this.y = window.scrollY || 0;
        if (this.afId) {
            cancelAnimationFrame(this.afId);
        }
        this.afId = requestAnimationFrame(this._onAnimationFrame);
    }
    private _onAnimationFrame() {
        this._refreshPositions();
        this.checkCurrent();
    }
    private _refreshHeights() {
        const { body } = document;
        if (!body) { return; }
        const { container, parts } = this.state;
        if (!container) { return; }
        const { current } = container;
        if (!current) { return; }

        this.h = current.parentElement && current.parentElement.clientHeight || 0;

        let contentScroll = 0;
        parts.forEach(s => {
            s.top = contentScroll;
            s.height = mezr.height(s.elt, 'margin');
            s.bottom = s.height + s.top
            contentScroll += s.height;
            s.stickies.forEach((sticky) => {
                const parent = sticky.elt.parentElement as HTMLElement;
                sticky.elt.style.transform = '';

                const parentHeight = mezr.height(parent, 'content');
                const height = mezr.height(sticky.elt, 'margin');
                const diff = parentHeight - height;
                if (diff < 0) {
                    sticky.startTop = -1;
                    return;
                }
                const offset = mezr.offset(sticky.elt, s.elt).top;
                sticky.startTop = s.top + offset;
                sticky.endTop = sticky.startTop + diff;

            });
            s.sections.forEach((section) => {
                const parent = section.elt.parentElement as HTMLElement;
                const offset = mezr.offset(section.elt, parent).top;
                section.top = s.top + offset;
                const height = mezr.height(section.elt, 'margin');
                section.bottom = section.top + height;
            });
        });

        const bodyHeight = body.parentElement!.clientHeight + contentScroll;
        body.style.height = `${bodyHeight}px`;

    }
    private _refresh() {
        this._refreshHeights();
        this._refreshPositions();
        this.checkCurrent();
    }

    private _registerPart(container: HTMLElement, url: string, title?: string) {
        const stickies =
            Array
                .from(container.querySelectorAll("*[data-sticky]"))
                .map((elt: HTMLElement) =>
                    ({ elt, endTop: 0, startTop: 0 })
                );

        const sections =
            Array
                .from(container.querySelectorAll("*[data-section]"))
                .map((elt: HTMLElement) =>
                    ({ id: elt.dataset.section || '', elt, top: 0, bottom: 0 })
                );

        const s = {
            elt: container,
            url,
            y: 0,
            height: 0,
            bottom: 0,
            top: 0,
            title,
            stickies,
            sections
        } as part;

        this.setState(
            ({ parts }) => ({ parts: [...parts, s] })
        );
    }
    private _unRegisterPart(url: string) {
        this.setState(
            ({ parts }) => ({ parts: parts.filter(s => s.url !== url) })
        );
    }
    private _refreshPositions() {

        const { state: { container, parts } } = this;
        const translatingContainer = container?.current;
        if (!translatingContainer) { return; }

        translatingContainer.style.transform = `translate3d(0,-${this.y}px,0)`;

        const isValid = (p: { top: number; bottom: number; }) => p && p.top < (this.y + this.h) && p.bottom > this.y;

        // find current = 
        const validParts = parts.filter((p) => isValid(p));

        for (const validPart of validParts) {
            for (const { startTop, endTop, elt } of validPart.stickies) {
                const t = startTop > this.y || startTop < 0 ? 0 :
                    Math.min(endTop - startTop, this.y - startTop);

                elt.style.transform = `translate3d(0,${t}px,0)`;

            }
        }
    }
    private _setCurrentUrl(currentUrl?: string) {
        const { history } = this.props;

        if (currentUrl) {
            history.push(`${currentUrl}`, "scroll");
        }
        this.setState(
            () => ({ currentUrl })
        );
    }

    // private _isAfter(s: part) {
    //     const { h } = this;
    //     const { elt } = s;
    //     elt.style.transform = `translate3d(0,${h}px,0)`;
    //     elt.dataset.position = 'after';
    //     this._stickyUp(s);
    // }
    // private _isBefore(s: part) {
    //     const { elt, height } = s;
    //     elt.style.transform = `translate3d(0,${-height}px,0)`;
    //     elt.dataset.position = 'before';
    //     this._stickyDown(s);
    // }

    // private _isFixedBottom(s: part) {
    //     const { h, y } = this;
    //     const { elt, height, top } = s;
    //     // const t = height - Math.min(h, max - height);
    //     elt.style.transform = `translate3d(0,${top - y}px,0)`;
    //     elt.dataset.position = 'fixed';
    //     this._stickyScroll(s, height - h);
    // }

    // private _isScrolling(s: part) {
    //     const { y } = this;
    //     const { elt, top } = s;
    //     elt.style.transform = `translate3d(0,${top - y}px,0)`;
    //     elt.dataset.position = 'scrolling';
    //     this._stickyScroll(s, y - top);
    // }
    // private _stickyUp(s: part) {
    //     const { stickies } = s;
    //     stickies.forEach(({ elt }) => {
    //         elt.style.transform = `translate3d(0,0,0)`;
    //     });
    // }

    // private _stickyDown(s: part) {
    //     const { stickies } = s;

    //     stickies.forEach(({ elt, height: stickyHeight, parentHeight }) => {
    //         elt.style.transform = `translate3d(0,${(parentHeight - stickyHeight)}px,0)`;
    //     });
    // }
    // private _stickyScroll(s: part, scroll: number) {
    //     const { stickies } = s;

    //     stickies.forEach(({ elt, height: stickyHeight, parentHeight }) => {
    //         const offsetTop = mezr.offset(elt.parentElement as HTMLElement, [s.elt, 'margin']).top;
    //         const t = Math.min(Math.max(0, scroll - offsetTop), (parentHeight - stickyHeight));

    //         elt.style.transform = `translate3d(0,${t}px,0)`;
    //     });
    // }

}

export type InjectedAppScrollerProps = { appScrollerCtx: AppScroller };

export const withAppScroller =
    <OriginalProps extends InjectedAppScrollerProps>(
        Component: React.ComponentType<OriginalProps>
    ): React.FunctionComponent<Subtract<OriginalProps, InjectedAppScrollerProps>> => {
        const C = (props: Subtract<OriginalProps, InjectedAppScrollerProps>) => {
            return (
                <AppScrollerReactContext.Consumer>
                    {(appScrollerCtx) => <Component {...{ ...(props as object), appScrollerCtx } as OriginalProps} />}
                </AppScrollerReactContext.Consumer>
            );
        };
        const wrappedName = Component.displayName || Component.name || 'Component';
        C.displayName = `WithAppScroller-${wrappedName}`;
        return C;
    };
export const AppScrollerContainer = withAppScroller(Container);

export default withRouter(AppScrollerProvider);


// WEBPACK FOOTER //
// ./src/_context/AppScroller.tsx